import { inject, Injectable } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import {
	ActionCbaLinkErrorLecteur,
	ActionCbaLinkErrorLecteurModal,
	ActionPluginErrorLecteur,
	ActionStoppedWithError,
	ActionSuccessLecteur,
	ActionWSErrorLecteur,
	AppelAdr,
	AppelInsi,
	AppelInterAMC,
	CallAdrWebService,
	CallAdrWebServiceSuccess,
	CallInsiWebService,
	CallInsiWebServiceSuccess,
	CallInterAMCWebService,
	CallMutedInsiWebService,
	DoNothingLecteur,
	EndActionLecteur,
	GetAssertionCPS,
	GetAssertionCPSSuccess,
	GetInsiActive,
	GetInsiActiveError,
	GetInsiActiveSuccess,
	HideLoaderLecteur,
	IncrementMissingUrlAmc,
	InitActionLecteur,
	InitLecteurWithAssertionPS,
	LecteurActionTypes,
	LecteurGenericError,
	LectureCV,
	ResetActionLecteur,
	ResetInited,
	SaveUrlAMC,
	SetBase64Lecteur,
	SetReponseLecteur,
	SetResult,
	SetResultLectureCVADR,
	StartActionLecteur,
	TraductionListeBeneficiaire,
	VerifyCPSValidity,
	VerifyCPSValidityCV,
	VerifyCPSValidityError,
} from "./lecteur.action";
import { catchError, concatMap, map, mergeMap, switchMap, tap, withLatestFrom } from "rxjs/operators";
import { Action, select, Store } from "@ngrx/store";
import {
	AppState,
	AskAdriInfoSuccess,
	CaisseHelperService,
	CreateCaisse,
	DecodageGroupeCvService,
	ProfileK300HelperService,
	selectAllCaisses,
	selectCurrentIdel,
	selectCurrentMutuelle,
	UpdateFromCV,
	UpdateMutuelleAttestationPatient,
} from "features";
import {
	AppelAdrParams,
	ErrorGroupe,
	MobileCaisse,
	OrdonnanceSelAmcTool,
	ReponseAdr,
	ReponseSelAmc,
	ReponseSelAmcAction,
	ReponseSelAmcParsed,
	ReponseUserResolveError,
	TypeErreurSelAmc,
	UrlAmc,
} from "@jack-sparrow-libraries/emotion-models";
import { forkJoin, from, Observable, of } from "rxjs";
import { CodeErreurEnum, LecteurService, TypeActionLecteur } from "@jack-sparrow-libraries/lib-agathe-link";
import { LecteurDialogService } from "@featuresAY/lecteur/services/lecteur-dialog.service";
import {
	isLastAction,
	selectAssertionCps,
	selectCurrentAction,
	selectData,
	selectErrorActionType,
	selectIsInsiActive,
	selectLecteurActionsOptions,
	selectLecteurCurrentIdAction,
	selectMissingUrlAmcCounter,
	selectResults,
	selectShouldRetry,
} from "./lecteur.selector";
import { v4 as uuidv4 } from "uuid";
import { environment } from "@env/environment";
import { LecteurApiService } from "../../services/api/lecteur-api.service";
import { CpsCheckCacheService } from "@featuresAY/lecteur/services/cps-check-cache.service";
import { AppelInsiParams, ReponseInsi, ReponseInsiAction, ReponseInsiParsed } from "@models/insi.models";
import { LecteurManagerService } from "../../services/lecteur-manager.service";
import { AnnuaireAmcUrl } from "@models/selamc.models";
import { LoadingAyWebService } from "@services/loading-ay-web.service";
import { OrdreCbaLinkError } from "@featuresAY/lecteur/enums/lecteur-enums";
import { AlertAyWebService } from "@services/alert-ay-web.service";
import { SelamcApiService } from "@featuresAY/shared/garantie-paiement/tlsi/tlsi-selamc/services/api/selamc-api.service";
import { SelamcReponseService } from "@featuresAY/shared/garantie-paiement/tlsi/tlsi-selamc/services/selamc-reponse.service";
import { SelamcAlertService } from "@featuresAY/shared/garantie-paiement/tlsi/tlsi-selamc/services/selamc-alert.service";
import { NavigationService } from "@services/navigation.service";

@Injectable()
export class LecteurEffect {
	constructor(
		private actions$: Actions,
		private store: Store<AppState>,
		private decodageGroupeCVService: DecodageGroupeCvService,
		private lecteurService: LecteurService,
		private lecteurDialogService: LecteurDialogService,
		private k300Helper: ProfileK300HelperService,
		private lecteurHelperService: LecteurManagerService,
		private alertService: AlertAyWebService,
		private selamcAlertService: SelamcAlertService,
		private lecteurApiService: LecteurApiService,
	) {}

	private _loadingService = inject(LoadingAyWebService);
	private selamcApiService = inject(SelamcApiService);
	private selamcReponseService = inject(SelamcReponseService);
	public readonly navigationService = inject(NavigationService);
	private readonly cpsCheckCache = inject(CpsCheckCacheService);

	// AY-10282 — discrimination de l'erreur /pscps/verifyCps : le back renvoie toujours HTTP 500,
	// seul le header custom `my-exception-code` (exposé en CORS) distingue un vrai problème de certificat
	// (révoqué / invalide) d'une erreur technique. Cf. TlsiException.Type + EmotionExceptionMapperProvider.
	private static readonly EXCEPTION_CODE_HEADER = "my-exception-code";
	private static readonly CPS_CERT_KO_CODES = ["T_ASSERTION_PS_CERTIFICAT_REVOQUE", "T_ASSERTION_PS_CERTIFICAT_INVALIDE"];

	initAction$ = createEffect(() =>
		this.actions$.pipe(
			ofType<InitActionLecteur>(LecteurActionTypes.InitAction),
			withLatestFrom(this.store.select(selectLecteurCurrentIdAction)),
			concatMap(([action, currentIdAction]: [InitActionLecteur, string]) => {
				if (currentIdAction !== action.payload.id) {
					return of({ action, param: null });
				}
				return of({ action, param: true });
			}),
			concatMap(({ action, param }) => {
				return this.lecteurHelperService.manageOptionHideLoaderObservableObject(action.payload.options, {
					action,
					param,
				});
			}),
			concatMap(({ action, param }) => {
				if (!param) {
					return of(new DoNothingLecteur());
				}
				return this.lecteurHelperService.manageOptionShowLoaderObservable(
					action.payload.options,
					new StartActionLecteur(),
				);
			}),
			catchError((error) => of(new LecteurGenericError(error))),
		),
	);

	startAction$ = createEffect(() =>
		this.actions$.pipe(
			ofType<StartActionLecteur>(LecteurActionTypes.StartAction),
			withLatestFrom(this.store.select(selectCurrentAction)),
			map(([action, currentAction]) => {
				return currentAction ? currentAction : new DoNothingLecteur();
			}),
			catchError((error) => of(new LecteurGenericError(error))),
		),
	);

	traductionListeBeneficiaire$ = createEffect(() =>
		this.actions$.pipe(
			ofType<TraductionListeBeneficiaire>(LecteurActionTypes.TraductionListeBeneficiaire),
			withLatestFrom(this.store.select(selectData), this.store.pipe(select(selectAllCaisses))),
			concatMap(([action, data, caisses]) => {
				// Decodage des groupes
				const groupeDecoderCv = this.decodageGroupeCVService.decodeBufferComplet(
					this.decodageGroupeCVService.base64ToArray(data),
				);
				// Conversion en structure supportDroit
				const result = this.decodageGroupeCVService.convertGroupeToStructureSupportDroit(groupeDecoderCv);
				const actions: Action[] = [new SetResultLectureCVADR(data), new ActionSuccessLecteur({ data: result })];

				if (
					!CaisseHelperService.isCaisseExist(caisses, CaisseHelperService.extractCaisseFromResultK300(result))
				) {
					actions.unshift(new CreateCaisse(CaisseHelperService.toMobileCaisseK300(result)));
				}
				return actions;
			}),
			catchError((error) => of(new LecteurGenericError(error))),
		),
	);

	lectureCV$ = createEffect(() =>
		this.actions$.pipe(
			ofType<LectureCV>(LecteurActionTypes.LectureCV),
			concatMap((action) => {
				return from(this.lecteurService.verificationConfig()).pipe(
					switchMap(() => {
						return from(this.lecteurService.runAction(TypeActionLecteur.lectureVitale, "20120601")).pipe(
							switchMap((result: any) => [
								new ActionSuccessLecteur({
									data: result,
								}),
								new SetBase64Lecteur(result),
								new UpdateFromCV(true),
							]),
							catchError((error: any) => [
								new ActionPluginErrorLecteur({ errorActionType: action.type }),
								new ActionCbaLinkErrorLecteur({
									error,
									message: `Erreur lors de la lecteur de la carte vitale`,
									canRetry: true,
								}),
							]),
						);
					}),
					catchError((error: any) => [
						new ActionPluginErrorLecteur({ errorActionType: action.type }),
						new ActionCbaLinkErrorLecteur({
							error,
							message: `Erreur lors de la lecteur de la carte vitale`,
							canRetry: true,
						}),
					]),
				);
			}),
			catchError((error) => of(new LecteurGenericError(error))),
		),
	);

	actionSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType<ActionSuccessLecteur>(LecteurActionTypes.ActionSuccess),
			withLatestFrom(this.store.select(isLastAction)),
			map(([action, isLastAction]) => {
				if (isLastAction) {
					return new HideLoaderLecteur(action.payload);
				}
				return new StartActionLecteur();
			}),
			catchError((error) => of(new LecteurGenericError(error))),
		),
	);

	hideLoader$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType<HideLoaderLecteur>(LecteurActionTypes.HideLoader),
				withLatestFrom(this.store.select(selectLecteurActionsOptions)),
				tap(async ([action, options]) => {
					this.store.dispatch(new EndActionLecteur());
					this.lecteurDialogService.hideReadLecteurLoading();
					if (options && options.onSuccess) {
						options.onSuccess(action.payload);
					}
					if (options && options.redirection && options.redirection.path) {
						this.navigationService.navAngularV2(
							options.redirection.path,
							options.redirection.navigationExtras,
						);
					}
				}),
				catchError((error) => of(new LecteurGenericError(error))),
			),
		{ dispatch: false },
	);

	actionFailure$ = createEffect(() =>
		this.actions$.pipe(
			ofType<LecteurGenericError>(LecteurActionTypes.LecteurGenericError),
			withLatestFrom(this.store.select(selectErrorActionType)),
			map(([action, actionType]) => {
				return new EndActionLecteur();
			}),
		),
	);

	pluginError$ = createEffect(() =>
		this.actions$.pipe(
			ofType<ActionPluginErrorLecteur>(LecteurActionTypes.ActionPluginError),
			withLatestFrom(selectErrorActionType),
			map(([action, actionType]) => {
				return new EndActionLecteur();
			}),
			catchError((error) => of(new LecteurGenericError(error))),
		),
	);

	resetInited$ = createEffect(() =>
		this.actions$.pipe(
			ofType<ResetInited>(LecteurActionTypes.ResetInited),
			map((action) => {
				return action.payload;
			}),
			catchError((error) => of(new LecteurGenericError(error))),
		),
	);

	getAssertionCPS$ = createEffect(() =>
		this.actions$.pipe(
			ofType<GetAssertionCPS>(LecteurActionTypes.GetAssertionCPS),
			withLatestFrom(this.store.select(selectCurrentIdel)),
			concatMap(([action, idel]) => {
				return from(
					this.lecteurService.runAction(TypeActionLecteur.getAssertionCPS, idel.idFact + idel.cleIdFact),
				).pipe(
					switchMap((result: any) => [
						new GetAssertionCPSSuccess(result),
						new ActionSuccessLecteur({ data: result }),
					]),
					catchError((error: any) =>
						of(
							new ActionCbaLinkErrorLecteur({
								error,
								message: `Erreur lors du login CPS`,
								canRetry: true,
							}),
						),
					),
				);
			}),
			catchError((error) => of(new LecteurGenericError(error))),
		),
	);

	appelAdr$ = createEffect(() =>
		this.actions$.pipe(
			ofType<AppelAdr>(LecteurActionTypes.AppelAdr),
			concatMap((action) => {
				return of(
					new ResetInited(
						new InitActionLecteur({
							id: uuidv4(),
							actions: [
								new GetAssertionCPS(),
								new VerifyCPSValidity(),
								new CallAdrWebService(action.payload),
								new GetInsiActive(action.payload.patientIdentite.patientId),
								new CallMutedInsiWebService(action.payload.patientIdentite),
							],
							needCodePorteur: true,
							options: {
								loader: {
									title: "Connexion à CBA link",
									message: "Nous communiquons à nouveau avec Ted...",
								},
							},
						}),
					),
				);
			}),
			catchError((error) => {
				return of(new DoNothingLecteur());
			}),
		),
	);

	appelInterAMC$ = createEffect(() =>
		this.actions$.pipe(
			ofType<AppelInterAMC>(LecteurActionTypes.AppelInterAMC),
			concatMap((action) => {
				return of(
					new ResetInited(
						new InitActionLecteur({
							id: uuidv4(),
							actions: [new GetAssertionCPS(), new CallInterAMCWebService(action.payload)],
							needCodePorteur: true,
							options: {
								loaderRetry: {
									title: "Connexion à CBA link",
									message: "Nous communiquons à nouveau avec Ted...",
								},
							},
						}),
					),
				);
			}),
			catchError((error) => {
				return of(new DoNothingLecteur());
			}),
		),
	);

	initLecteurWithAssertionPS$ = createEffect(() =>
		this.actions$.pipe(
			ofType<InitLecteurWithAssertionPS>(LecteurActionTypes.InitLecteurWithAssertionPS),
			concatMap((action) => {
				return of(
					new ResetInited(
						new InitActionLecteur({
							id: uuidv4(),
							actions: [new GetAssertionCPS()],
							needCodePorteur: true,
							options: {
								loaderRetry: {
									title: "Connexion à CBA link",
									message: "Nous communiquons à nouveau avec ton lecteur...",
								},
							},
						}),
					),
				);
			}),
			catchError((error) =>
				of(
					new ActionStoppedWithError({
						action: error,
					}),
				),
			),
		),
	);

	appelInsi$ = createEffect(() =>
		this.actions$.pipe(
			ofType<AppelInsi>(LecteurActionTypes.AppelInsi),
			concatMap((action) => {
				return of(
					new ResetInited(
						new InitActionLecteur({
							id: uuidv4(),
							actions: [new GetAssertionCPS(), new CallInsiWebService(action.payload)],
							needCodePorteur: true,
							options: {
								loaderRetry: {
									title: "Connexion à CBA link",
									message: "Nous communiquons à nouveau avec Ted...",
								},
							},
						}),
					),
				);
			}),
			catchError((error) => {
				return of(new DoNothingLecteur());
			}),
		),
	);

	verifyCpsPerimee$ = createEffect(() =>
		this.actions$.pipe(
			ofType<VerifyCPSValidity>(LecteurActionTypes.VerifyCPSValidity),
			withLatestFrom(this.store.select(selectAssertionCps)),
			concatMap(([action, assertionCps]) => {
				const decodedAssertion = atob(assertionCps);
				return this.lecteurApiService.getCpsValidity(decodedAssertion).pipe(
					mergeMap(() => of(new ActionSuccessLecteur({}))),
					catchError((error: any) => [
						new ActionStoppedWithError({
							action: action.type,
						}),
						new VerifyCPSValidityError(error),
					]),
				);
			}),
		),
	);

	/**
	 * AY-10282 — Vérification préventive du certificat CPS intercalée AVANT la lecture de la carte
	 * Vitale (création patient). Réutilise le code porteur déjà saisi pour le batch (aucune saisie
	 * supplémentaire) : GetAssertionCPS a produit l'assertion, on la vérifie via /pscps/verifyCps.
	 * - certificat valide  → ActionSuccessLecteur → la chaîne avance vers LectureCV (lecture Vitale) ;
	 * - certificat KO       → même modale V2 que l'ADR (Continuer / Annuler) :
	 *     • Continuer → on relance le loader puis ActionSuccessLecteur (la lecture Vitale se poursuit) ;
	 *     • Annuler   → ResetActionLecteur + ActionStoppedWithError (pas de lecture, pas de création).
	 */
	verifyCpsPerimeeCV$ = createEffect(() =>
		this.actions$.pipe(
			ofType<VerifyCPSValidityCV>(LecteurActionTypes.VerifyCPSValidityCV),
			withLatestFrom(this.store.select(selectAssertionCps), this.store.select(selectCurrentIdel)),
			concatMap(([action, assertionCps, idel]) => {
				// Séparateur explicite : sur poste mutualisé, deux PS ne doivent jamais produire la même clé
				// (ex. idFact=12/cleIdFact=34 vs idFact=1/cleIdFact=234 → "12-34" ≠ "1-234").
				const psKey = idel ? `${idel.idFact}-${idel.cleIdFact}` : "";
				// Cache 1h (AY-10282) : si une décision récente existe POUR CE praticien (certif valide, ou KO
				// déjà acquitté via "Continuer"), on saute la vérif ET la modale → lecture Vitale directe.
				if (this.cpsCheckCache.isFresh(psKey)) {
					return of(new ActionSuccessLecteur({}));
				}
				if (!assertionCps) {
					// Assertion absente (anormal : GetAssertionCPS aurait dû la produire juste avant) → on n'alerte
					// pas et on laisse la création patient se poursuivre.
					return of(new ActionSuccessLecteur({}));
				}
				let decodedAssertion: string;
				try {
					decodedAssertion = atob(assertionCps);
				} catch {
					// Assertion base64 illisible : on ne fait remonter aucune erreur au flux (sinon le catchError
					// terminal complèterait l'effect et le tuerait pour toute la session) → lecture Vitale directe.
					console.warn("[CPS] assertion illisible, vérification préventive ignorée");
					return of(new ActionSuccessLecteur({}));
				}
				return this.lecteurApiService.getCpsValidity(decodedAssertion).pipe(
					mergeMap(() => {
						this.cpsCheckCache.write(psKey); // certif valide (HTTP 204) → cache 1h
						return of(new ActionSuccessLecteur({}));
					}),
					catchError((err: HttpErrorResponse) => {
						// On ne déclenche la modale "révoqué" QUE sur un vrai problème de certificat, identifié par le
						// header back `my-exception-code`. Une erreur technique (500 sans code, réseau, timeout) ne doit
						// ni afficher de fausse alerte, ni être mise en cache : la création patient se poursuit.
						const code = err?.headers?.get(LecteurEffect.EXCEPTION_CODE_HEADER);
						if (code && LecteurEffect.CPS_CERT_KO_CODES.includes(code)) {
							return from(this.resolveVerifyCpsCVError(action.type, psKey)).pipe(concatMap((actions) => actions));
						}
						console.warn("[CPS] vérif préventive ignorée (erreur non-certificat)", err?.status, code);
						return of(new ActionSuccessLecteur({}));
					}),
				);
			}),
			catchError((error) => of(new LecteurGenericError(error))),
		),
	);

	/** Affiche la modale d'erreur certificat CPS (Continuer / Annuler) et résout la suite de la chaîne. */
	private async resolveVerifyCpsCVError(actionType: string, psKey: string): Promise<Action[]> {
		await this.lecteurDialogService.hideReadLecteurLoading();
		const res = await this.lecteurDialogService.showCpsCertificatInvalideDialog();
		if (res === OrdreCbaLinkError.CONTINUE) {
			// L'utilisateur continue malgré le certificat KO → on mémorise sa décision (cache 1h, clé par PS)
			// pour ne pas ré-afficher la modale à chaque création patient, puis on relance le loader et la Vitale.
			this.cpsCheckCache.write(psKey);
			await this.lecteurDialogService.showLoading("Connexion à CBA link");
			return [new ActionSuccessLecteur({})];
		}
		// Annulation → on stoppe la chaîne (ni lecture Vitale, ni création) et on NE cache PAS (re-check au prochain essai).
		return [new ResetActionLecteur(), new ActionStoppedWithError({ action: actionType })];
	}

	callAdrWebService$ = createEffect(() =>
		this.actions$.pipe(
			ofType<CallAdrWebService>(LecteurActionTypes.CallAdrWebService),
			withLatestFrom(this.store.select(selectAssertionCps), this.store.select(selectCurrentIdel)),
			concatMap(([action, assertionCps, currentIdel]) => {
				const appelAdrParams: AppelAdrParams = {
					dateReference: action.payload.dateReference,
					manuel: action.payload.manuel,
					patientIdentite: action.payload.patientIdentite,
					situationId: currentIdel.id,
					typeOs: "WIN", // TODO
					assertionPs: assertionCps,
				};

				return this.k300Helper
					.startWsAction(
						"Appel ADR",
						`${environment.apiEmotion()}/tlsi/adri/appelAdr`,
						"POST",
						appelAdrParams,
						true,
					)
					.pipe(
						withLatestFrom(this.store.pipe(select(selectAllCaisses))),
						concatMap(([reponseAdr, caisses]: [ReponseAdr, MobileCaisse[]]) => {
							const groupeDecoderCv = this.decodageGroupeCVService.decodeBufferComplet(
								this.decodageGroupeCVService.base64ToArray(reponseAdr.supportData),
							);
							const result =
								this.decodageGroupeCVService.convertGroupeToStructureSupportDroit(groupeDecoderCv);
							const actions: Action[] = [
								new SetResultLectureCVADR(reponseAdr.supportData),
								new SetResult(result),
								new SetReponseLecteur({ reponseAdr: reponseAdr }),
								new AskAdriInfoSuccess(result),
								new CallAdrWebServiceSuccess(reponseAdr),
								new ActionSuccessLecteur({ data: reponseAdr }),
							];

							if (
								result.regime !== null &&
								result.codeCaisse !== null &&
								result.codeCentre !== null &&
								!CaisseHelperService.isCaisseExist(
									caisses,
									CaisseHelperService.extractCaisseFromResultK300(result),
								)
							) {
								actions.unshift(new CreateCaisse(CaisseHelperService.toMobileCaisseK300(result)));
							}
							return actions;
						}),
						catchError((error) =>
							of(
								new ActionWSErrorLecteur({
									error,
									message: "Erreur lors de l'appel ADR",
								}),
							),
						),
					);
			}),
			catchError((error) => {
				return of(new LecteurGenericError(error));
			}),
		),
	);

	callInsiWebService$ = createEffect(() =>
		this.actions$.pipe(
			ofType<CallInsiWebService>(LecteurActionTypes.CallInsiWebService),
			withLatestFrom(this.store.select(selectAssertionCps), this.store.select(selectCurrentIdel)),
			concatMap(([action, assertionCps, currentIdel]) => {
				const appelInsiParams: AppelInsiParams = {
					situationId: currentIdel.id,
					typeOs: "WIN", // TODO
					assertionPs: assertionCps,
					patientInsiInfo: action.payload,
				};

				return this.k300Helper
					.startWsAction(
						"Appel Insi",
						`${environment.apiEmotion()}/tlsi/insi/rechercherOuVerifierIns`,
						"POST",
						appelInsiParams,
						true,
					)
					.pipe(
						concatMap((reponseInsi: ReponseInsi) => {
							// TODO traiter la reponse Insi
							return from(this.lecteurHelperService.traiteReponseInsi(reponseInsi)).pipe(
								concatMap((responseInsiRetour: ReponseInsiParsed) => {
									// TODO should change the responseInsiRetour type to ReponseInsiParsed and treat it like the inter amc case

									if (responseInsiRetour.returnAction === ReponseInsiAction.ERROR) {
										return of(
											new ActionSuccessLecteur({
												data: { typeErreur: responseInsiRetour.typeErreur },
											}),
										);
									} else if (responseInsiRetour.returnAction === ReponseInsiAction.PARAMS_MISSING) {
										return from(
											this.lecteurHelperService.resolveInsiPlusieursIdentitesTrouves(),
										).pipe(
											map((numCommune) => {
												if (numCommune) {
													return new CallInsiWebService({
														...appelInsiParams.patientInsiInfo,
														lieuNaissance: +numCommune,
													});
												}
												return new ActionSuccessLecteur({
													data: { typeErreur: responseInsiRetour.typeErreur },
												});
											}),
										);
									}
									return [
										new CallInsiWebServiceSuccess(reponseInsi?.insActif),
										new ActionSuccessLecteur({
											data: { typeErreur: responseInsiRetour.typeErreur },
										}),
									];
								}),
							);
						}),
						catchError((error) =>
							of(
								new ActionWSErrorLecteur({
									error,
									message: "Erreur lors de l'appel ADR",
								}),
							),
						),
					);
			}),
			catchError((error) => {
				return of(new LecteurGenericError(error));
			}),
		),
	);

	callMutedInsiWebService$ = createEffect(() =>
		this.actions$.pipe(
			ofType<CallMutedInsiWebService>(LecteurActionTypes.CallMutedInsiWebService),
			withLatestFrom(
				this.store.select(selectAssertionCps),
				this.store.select(selectCurrentIdel),
				this.store.select(selectIsInsiActive),
				this.store.select(selectResults),
			),
			concatMap(([action, assertionCps, currentIdel, insiActif, results]) => {
				if (insiActif) return of(new ActionSuccessLecteur({ data: results }));
				const appelInsiParams: AppelInsiParams = {
					situationId: currentIdel.id,
					typeOs: "WIN",
					assertionPs: assertionCps,
					patientId: action.payload.patientId,
				};
				return this.k300Helper
					.startWsAction(
						"Appel Insi",
						`${environment.apiEmotion()}/tlsi/insi/rechercherOuVerifierIns`,
						"POST",
						appelInsiParams,
						true,
					)
					.pipe(
						concatMap(() => of(new ActionSuccessLecteur({ data: results }))),
						catchError(() => of(new ActionSuccessLecteur({ data: results }))),
					);
			}),
			catchError((error) => {
				return of(new LecteurGenericError(error));
			}),
		),
	);

	callInsiActive$ = createEffect(() =>
		this.actions$.pipe(
			ofType<GetInsiActive>(LecteurActionTypes.GetInsiActive),
			withLatestFrom(this.store.select(selectResults)),
			concatMap(([action, results]) => {
				const patientId = action.payload;
				return this.k300Helper
					.startWsAction(
						"Get insi",
						`${environment.apiEmotion()}/tlsi/insi/patient/${patientId}/actif`,
						"GET",
						null,
						true,
					)
					.pipe(
						switchMap((response) => {
							return [
								new GetInsiActiveSuccess(response?.actif ?? response),
								new ActionSuccessLecteur({ data: results }),
							];
						}),
						catchError((error) => [
							new GetInsiActiveError(error),
							new ActionSuccessLecteur({ data: results }),
						]),
					);
			}),
		),
	);

	callSelAmcWebService$ = createEffect(() =>
		this.actions$.pipe(
			ofType<CallInterAMCWebService>(LecteurActionTypes.CallInterAMCWebService),
			withLatestFrom(
				this.store.select(selectAssertionCps),
				this.store.select(selectCurrentIdel),
				this.store.select(selectCurrentMutuelle),
				this.store.select(selectMissingUrlAmcCounter),
			),
			concatMap(([action, assertionCps, currentIdel, mutuelle, missingUrlCounter]) => {
				// si on veut simuler un soucis sur notre certificat CPS
				// const certificatCpsModifieByte = window.atob(assertionCps.certificatCps).replace('http', 'aaaa');
				const ordonnanceSelAmcTool: OrdonnanceSelAmcTool = {
					...action.payload?.ordoSelAmcTool,
					assertionPs: assertionCps,
				};

				const erreurTechBloquante = action.payload?.fromOrdo;

				return this.selamcApiService.callServiceIdb(ordonnanceSelAmcTool, currentIdel.id).pipe(
					concatMap((reponseSelAmc: ReponseSelAmc) => {
						return from(
							this.selamcReponseService.traiteReponseSelAmc(
								reponseSelAmc,
								erreurTechBloquante,
								missingUrlCounter,
							),
						).pipe(
							concatMap((reponseSelAmcRetour: ReponseSelAmcParsed) => {
								if (reponseSelAmcRetour.returnAction === ReponseSelAmcAction.SUCCESS) {
									reponseSelAmc.urlAmc = this.selamcReponseService.buildUrlAmc(reponseSelAmc.urlAmc);

									return from(this.selamcApiService.traiteReponseSelAmcIdb(reponseSelAmc)).pipe(
										mergeMap((mutuelleAttestationPatient) => [
											new ActionSuccessLecteur({
												data: { typeErreur: TypeErreurSelAmc.Aucune },
											}),
											new SetReponseLecteur({
												reponseDataInterAmc: {
													err: null,
													typeErreur: TypeErreurSelAmc.Aucune,
												},
											}),
											new UpdateMutuelleAttestationPatient({
												...mutuelleAttestationPatient,
												utiliseSelAmc: true,
												version: null,
											}),
										]),
									);
								}

								if (reponseSelAmcRetour.returnAction === ReponseSelAmcAction.URL_SELECT) {
									return of(
										new CallInterAMCWebService({
											ordoSelAmcTool: {
												...action.payload?.ordoSelAmcTool,
												urlAmc: reponseSelAmcRetour.amcJson,
											},
											fromOrdo: erreurTechBloquante,
										}),
									);
								}
								if (reponseSelAmcRetour.returnAction === ReponseSelAmcAction.URL_MISSING) {
									const params = {
										...action.payload?.ordoSelAmcTool,
										urlAmc: reponseSelAmcRetour.amcJson,
										annuaireAgreementSiteVersion: reponseSelAmcRetour.versionAgreeAnnuaireAmc,
									};
									return from(this.lecteurService.callUrlServiceAMC(params)).pipe(
										switchMap((result: any) => {
											return !result
												? from(
														this.selamcAlertService.alertErrorMaintenanceInterAMC(
															mutuelle,
															ordonnanceSelAmcTool.fromRejet,
														),
													).pipe(
														mergeMap((err) =>
															from([
																new ActionSuccessLecteur({
																	data: {
																		err,
																		typeErreur: TypeErreurSelAmc.Technique,
																	},
																}),
																new SetReponseLecteur({
																	reponseDataInterAmc: {
																		err,
																		typeErreur: TypeErreurSelAmc.Technique,
																	},
																}),
															]),
														),
													)
												: [
														new IncrementMissingUrlAmc(),
														new CallInterAMCWebService({
															ordoSelAmcTool: result.appelSelAmcParams,
															fromOrdo: erreurTechBloquante,
														}),
														new SaveUrlAMC(
															result.listUrlAmcAndParamsToSave as {
																paramAppelAnnuaireToSave: UrlAmc;
																listeUrlAMC: UrlAmc[];
															}[],
														),
													];
										}),
										catchError((err) => {
											if (erreurTechBloquante) {
												return from(
													this.selamcAlertService.alertErrorMaintenanceInterAMC(
														mutuelle,
														ordonnanceSelAmcTool.fromRejet,
													),
												).pipe(
													mergeMap((err) =>
														from([
															new ActionSuccessLecteur({
																data: {
																	err,
																	typeErreur: TypeErreurSelAmc.Technique,
																},
															}),
															new SetReponseLecteur({
																reponseDataInterAmc: {
																	err,
																	typeErreur: TypeErreurSelAmc.Technique,
																},
															}),
														]),
													),
												);
											} else {
												return from([
													new ActionSuccessLecteur({
														data: {
															err,
															typeErreur: TypeErreurSelAmc.TechniqueNonBloquantSilencieux,
														},
													}),
													new SetReponseLecteur({
														reponseDataInterAmc: {
															err,
															typeErreur: TypeErreurSelAmc.TechniqueNonBloquantSilencieux,
														},
													}),
												]);
											}
										}),
									);
								}
								if (reponseSelAmcRetour.returnAction === ReponseSelAmcAction.ERROR) {
									if (ordonnanceSelAmcTool.silent) {
										return from([
											new ActionSuccessLecteur({
												data: {
													err: null,
													typeErreur: reponseSelAmcRetour.typeErreur,
												},
											}),
											new SetReponseLecteur({
												reponseDataInterAmc: {
													err: null,
													typeErreur: reponseSelAmcRetour.typeErreur,
												},
											}),
										]);
									}

									if (reponseSelAmcRetour.typeErreur === TypeErreurSelAmc.Technique) {
										return from(
											this.selamcAlertService.alertErrorMaintenanceInterAMC(
												mutuelle,
												ordonnanceSelAmcTool.fromRejet,
											),
										).pipe(
											mergeMap((err) =>
												from([
													new ActionSuccessLecteur({
														data: {
															err,
															typeErreur: TypeErreurSelAmc.Technique,
														},
													}),
													new SetReponseLecteur({
														reponseDataInterAmc: {
															err,
															typeErreur: TypeErreurSelAmc.Technique,
														},
													}),
												]),
											),
										);
									}

									// @JM REV-5343 on intercept les erruer de certificat CPS pour contrer les pb de caractères spéciaux qu'on a avec le K300
									if (
										reponseSelAmcRetour.typeErreur ===
										TypeErreurSelAmc.TechniqueNonBloquantSilencieux
									) {
										return from([
											new ActionSuccessLecteur({
												data: {
													err: null,
													typeErreur: TypeErreurSelAmc.TechniqueNonBloquantSilencieux,
												},
											}),
											new SetReponseLecteur({
												reponseDataInterAmc: {
													err: null,
													typeErreur: TypeErreurSelAmc.TechniqueNonBloquantSilencieux,
												},
											}),
										]);
									}
									if (reponseSelAmcRetour.typeErreur === TypeErreurSelAmc.TechniqueNonBloquant) {
										return from(
											this.selamcAlertService.alertErrorTechniqueNonBloquantInterAMC(
												reponseSelAmcRetour.returnMessage,
											),
										).pipe(
											mergeMap((err) =>
												from([
													new ActionSuccessLecteur({
														data: {
															err,
															typeErreur: TypeErreurSelAmc.TechniqueNonBloquant,
														},
													}),
													new SetReponseLecteur({
														reponseDataInterAmc: {
															err: err,
															typeErreur: TypeErreurSelAmc.TechniqueNonBloquant,
														},
													}),
												]),
											),
										);
									}

									if (reponseSelAmcRetour.typeErreur === TypeErreurSelAmc.Abandon) {
										return from([
											new ActionSuccessLecteur({
												data: {
													err: ReponseUserResolveError.ABANDON,
													typeErreur: TypeErreurSelAmc.Abandon,
												},
											}),
											new SetReponseLecteur({
												reponseDataInterAmc: {
													err: ReponseUserResolveError.ABANDON,
													typeErreur: TypeErreurSelAmc.Abandon,
												},
											}),
										]);
									}

									if (reponseSelAmcRetour.typeErreur === TypeErreurSelAmc.Fonctionnelle) {
										return from(
											reponseSelAmcRetour.diagnosticSelAmc.errorsGroupe ===
												ErrorGroupe.VERIFIER_CONVENTIONNEMENT_PS
												? this.selamcAlertService.alertErrorFonctionnelConventionInterAMC(
														mutuelle,
													)
												: this.selamcAlertService.alertErrorFonctionnelInterAMC(
														reponseSelAmcRetour.diagnosticSelAmc,
														mutuelle,
														ordonnanceSelAmcTool.patientId,
														ordonnanceSelAmcTool.fromRejet,
													),
										).pipe(
											mergeMap((err) =>
												from([
													new ActionSuccessLecteur({
														data: {
															err,
															typeErreur: TypeErreurSelAmc.Technique,
														},
													}),
													new SetReponseLecteur({
														reponseDataInterAmc: {
															err,
															typeErreur: TypeErreurSelAmc.Technique,
														},
													}),
												]),
											),
										);
									}
								}

								return from([
									new ActionSuccessLecteur({
										data: { err: null, typeErreur: TypeErreurSelAmc.Aucune },
									}),
									new SetReponseLecteur({
										reponseDataInterAmc: { err: null, typeErreur: TypeErreurSelAmc.Aucune },
									}),
								]);
							}),
						);
					}),
					catchError((error) =>
						from([
							new ActionSuccessLecteur({
								data: {
									error,
									typeErreur: TypeErreurSelAmc.TechniqueNonBloquantSilencieux,
								},
							}),
							new SetReponseLecteur({
								reponseDataInterAmc: {
									error,
									typeErreur: TypeErreurSelAmc.TechniqueNonBloquantSilencieux,
								},
							}),
						]),
					),
				);
			}),
		),
	);

	saveUrlAmc$ = createEffect(() =>
		this.actions$.pipe(
			ofType<SaveUrlAMC>(LecteurActionTypes.SaveUrlAMC),
			switchMap((action) => {
				const obs: Observable<any>[] = [];
				const listUrlAmcAndParamsToSave = action.payload;
				listUrlAmcAndParamsToSave.forEach((e) => {
					const paramAppel = e.paramAppelAnnuaireToSave;
					let listUrlAmcToSave = e.listeUrlAMC as any[];
					if (listUrlAmcToSave) {
						listUrlAmcToSave = Array.from(new Set(listUrlAmcToSave));
					} else {
						return of(new DoNothingLecteur());
					}
					listUrlAmcToSave.forEach((urlAmcToSave) => {
						const annuaireAmcUrl: AnnuaireAmcUrl = {
							critereAmc: paramAppel.amc,
							critereConv: paramAppel.typeConv,
							critereCsr: paramAppel.csr,
							critereService: paramAppel.service,
							domaineConventionnel: paramAppel.domaineConventionnel,
							url: urlAmcToSave.url,
							conv: urlAmcToSave.typeConv,
							csr: urlAmcToSave.csr,
							csrLibelle: urlAmcToSave.csrLibelle,
							reseauLibelle: urlAmcToSave.reseauLibelle,
							commentaire: urlAmcToSave.commentaire,
							contactMail: urlAmcToSave.contactMail,
							contactNom: urlAmcToSave.contactNom,
							contactPrenom: urlAmcToSave.contactPrenom,
							contactTel: urlAmcToSave.contactTel,
							dureeValidite: urlAmcToSave.dureeValidite,
						};
						obs.push(this.selamcApiService.saveUrlAmc(annuaireAmcUrl));
					});
				});
				return forkJoin(obs).pipe(
					mergeMap(() => of(new DoNothingLecteur())),
					catchError((error: any) => of(new DoNothingLecteur())),
				);
			}),
			catchError((err) => {
				console.error(err);
				return of(new DoNothingLecteur());
			}),
		),
	);

	actionWsError$ = createEffect(() =>
		this.actions$.pipe(
			ofType<ActionWSErrorLecteur>(LecteurActionTypes.ActionWSError),
			withLatestFrom(this.store.select(selectCurrentAction)),
			switchMap(([{ payload }, currentAction]) => {
				this.lecteurDialogService.hideReadLecteurLoading();
				if (payload.error.code === 9999) {
					if (payload.cancelHandler) {
						payload.cancelHandler();
					}
				} else {
					if (currentAction.type !== LecteurActionTypes.CallAdrWebService) {
						this.alertService
							.showErreur(
								`${payload.message} :<br>${payload.error.message ? payload.error.message : "Erreur inconnue"}`,
								"Erreur serveur",
							)
							.then(() => {
								if (payload.errorHandle) {
									payload.errorHandle();
								}
							});
					} else {
						if (payload.errorHandle) {
							payload.errorHandle();
						}
					}
				}
				return [
					new ResetActionLecteur(),
					new ActionStoppedWithError({
						error: payload.error.message,
						action: currentAction.type,
						code: payload?.error?.code,
					}),
				];
			}),
			catchError((error) => of(new LecteurGenericError(error))),
		),
	);

	actionCbaLinkError$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType<ActionCbaLinkErrorLecteur>(LecteurActionTypes.ActionCbaLinkError),
				withLatestFrom(this.store.select(selectLecteurActionsOptions), this.store.select(selectShouldRetry)),
				tap(([{ payload }, options, shouldRetry]) => {
					// TODO a voir si on doit gérer le retry selon les droits clients comme dans le mobile ( à voir effects actionPluginError$)
					// if (
					// 	!shouldRetry &&
					// 	payload.error.code === CodeErreurEnum.agatheLinkIntrouvable &&
					// 	payload.canRetry
					// ) {
					// 	this.store.dispatch(new StartActionLecteur());
					// 	this.store.dispatch(new IncrementShouldRetry(shouldRetry));
					// 	return;
					// }
					this._loadingService.hide().then(() => {
						this.lecteurHelperService.manageOptionHideLoader(options, () => {
							this.store.dispatch(new ActionCbaLinkErrorLecteurModal(payload));
						});
					});
				}),
				catchError((error) => of(new LecteurGenericError(error))),
			),
		{ dispatch: false },
	);

	actionLecteurErrorModal$ = createEffect(
		() => {
			return this.actions$.pipe(
				ofType<ActionCbaLinkErrorLecteurModal>(LecteurActionTypes.ActionCbaLinkErrorModal),
				withLatestFrom(this.store.select(selectLecteurActionsOptions), this.store.select(selectCurrentAction)),
				tap(async ([{ payload }, options, currentAction]) => {
					await this._loadingService.hide();

					if (
						payload.error?.code === CodeErreurEnum.agatheLinkIntrouvable &&
						options?.skipEverythingOnError
					) {
						this.store.dispatch(new ResetActionLecteur());
						this.store.dispatch(
							new ActionStoppedWithError({
								error: `${payload.error?.message ?? payload.message ?? "Erreur inconnue"}`,
								action: currentAction.type,
								code: payload.error?.code,
							}),
						);
						if (payload.errorHandle) {
							payload.errorHandle();
						}
						return;
					}
					const res = await this.lecteurDialogService.showModalFromError(payload.error, true);
					if (res === OrdreCbaLinkError.RETRY) {
						this.lecteurHelperService.manageOptionHideLoader(options, () => {
							this.store.dispatch(new StartActionLecteur());
						});
					} else {
						this._loadingService.hide();
						this.store.dispatch(new ResetActionLecteur());
						this.store.dispatch(
							new ActionStoppedWithError({
								error: `${payload.error?.message ?? payload.message ?? "Erreur inconnue"}`,
								action: currentAction.type,
								code: payload.error?.code,
							}),
						);
						if (payload.errorHandle) {
							payload.errorHandle();
						}
					}
				}),
				catchError((error) => of(new LecteurGenericError(error))),
			);
		},
		{ dispatch: false },
	);
}
